import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { mountainLogService } from '../services/mountainLogService';
import { LIST_ORDER, MOUNTAIN_LISTS } from '../data/mountainData';
import LoadingSpinner from '../components/LoadingSpinner';
import './MountainLog.css';

const DEBOUNCE_MS = 800;

function MountainLog() {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState(LIST_ORDER[0]);
  const [mountains, setMountains] = useState([]);
  const [userLogs, setUserLogs] = useState({});
  const [loadingMountains, setLoadingMountains] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [search, setSearch] = useState('');
  const [prefFilter, setPrefFilter] = useState('');
  const [climbedFilter, setClimbedFilter] = useState('all'); // 'all' | 'climbed' | 'unclimbed'
  const [expandedNo, setExpandedNo] = useState(null);
  const [, setEditValues] = useState({});
  const saveTimers = useRef({});

  // 山リストデータの取得
  useEffect(() => {
    let cancelled = false;
    setLoadingMountains(true);
    mountainLogService.getMountainList(activeTab).then((data) => {
      if (!cancelled && data) {
        setMountains(data.mountains || []);
      }
      if (!cancelled) setLoadingMountains(false);
    });
    return () => { cancelled = true; };
  }, [activeTab]);

  // ログデータの取得
  useEffect(() => {
    if (!currentUser) { setUserLogs({}); return; }
    let cancelled = false;
    setLoadingLogs(true);
    mountainLogService.getUserLogs(currentUser.uid, activeTab).then((logs) => {
      if (!cancelled) {
        setUserLogs(logs);
        setLoadingLogs(false);
      }
    });
    return () => { cancelled = true; };
  }, [currentUser, activeTab]);

  // タブ切り替え時にフィルターと展開状態をリセット
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setExpandedNo(null);
    setSearch('');
    setPrefFilter('');
    setClimbedFilter('all');
    setEditValues({});
  };

  // 登頂チェックボックスのトグル（クロスリスト同期付き）
  const handleClimbedToggle = useCallback(async (no, mountainName) => {
    if (!currentUser) return;
    const newClimbed = !userLogs[no]?.climbed;

    // オプティミスティック UI 更新（現在タブ）
    setUserLogs((prev) => {
      if (!newClimbed) {
        const next = { ...prev };
        delete next[no];
        return next;
      }
      return { ...prev, [no]: { ...prev[no], climbed: true } };
    });

    // Firestore 保存＋クロスリスト同期（失敗してもクラッシュしない）
    mountainLogService
      .saveClimbedWithSync(currentUser.uid, activeTab, no, mountainName, newClimbed)
      .catch(console.error);
  }, [currentUser, activeTab, userLogs]);

  // 編集フィールドの変更（デバウンスして保存）
  const handleFieldChange = useCallback((no, field, value) => {
    if (!currentUser) return;
    setEditValues((prev) => ({ ...prev, [`${no}_${field}`]: value }));

    // ユーザーログをオプティミスティックに更新
    setUserLogs((prev) => ({
      ...prev,
      [no]: { ...prev[no], [field]: value, climbed: true },
    }));

    // デバウンス保存
    const key = `${no}_${field}`;
    if (saveTimers.current[key]) clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(async () => {
      await mountainLogService.saveLog(currentUser.uid, activeTab, no, {
        climbed: true,
        [field]: value,
      });
    }, DEBOUNCE_MS);
  }, [currentUser, activeTab]);

  // 都道府県リスト（現在のリストから抽出）
  const prefectures = React.useMemo(() => {
    const prefs = new Set();
    mountains.forEach((m) => {
      m.prefecture.split(/[・、,]/).forEach((p) => prefs.add(p.trim()));
    });
    return Array.from(prefs).sort();
  }, [mountains]);

  // フィルタリング
  const filteredMountains = React.useMemo(() => {
    return mountains.filter((m) => {
      if (search) {
        const q = search.toLowerCase();
        if (
          !m.name.includes(search) &&
          !m.yomi.includes(q) &&
          !String(m.no).includes(q)
        ) return false;
      }
      if (prefFilter && !m.prefecture.includes(prefFilter)) return false;
      const log = userLogs[m.no];
      if (climbedFilter === 'climbed' && !log?.climbed) return false;
      if (climbedFilter === 'unclimbed' && log?.climbed) return false;
      return true;
    });
  }, [mountains, userLogs, search, prefFilter, climbedFilter]);

  // 登頂済み数
  const climbedCount = React.useMemo(() => {
    return mountains.filter((m) => userLogs[m.no]?.climbed).length;
  }, [mountains, userLogs]);

  const listMeta = MOUNTAIN_LISTS[activeTab];

  return (
    <div className="mountain-log">
      <div className="mountain-log-header">
        <h1 className="mountain-log-title">
          <span className="mountain-log-icon">⛰️</span>
          登頂記録
        </h1>
        {!currentUser && (
          <p className="mountain-log-notice">
            ログインすると登頂記録が保存されます
          </p>
        )}
      </div>

      {/* タブナビゲーション */}
      <div className="mountain-log-tabs">
        {LIST_ORDER.map((listId) => (
          <button
            key={listId}
            className={`mountain-log-tab ${activeTab === listId ? 'active' : ''}`}
            onClick={() => handleTabChange(listId)}
          >
            {MOUNTAIN_LISTS[listId].shortName}
          </button>
        ))}
      </div>

      {/* プログレス */}
      <div className="mountain-log-progress-section">
        <div className="mountain-log-progress-header">
          <span className="mountain-log-list-name">{listMeta?.name}</span>
          <span className="mountain-log-progress-count">
            <strong>{climbedCount}</strong> / {mountains.length} 座
          </span>
        </div>
        <div className="mountain-log-progress-bar-wrap">
          <div
            className="mountain-log-progress-bar"
            style={{ width: mountains.length ? `${(climbedCount / mountains.length) * 100}%` : '0%' }}
          />
        </div>
        <div className="mountain-log-progress-pct">
          {mountains.length ? Math.round((climbedCount / mountains.length) * 100) : 0}% 達成
        </div>
      </div>

      {/* フィルターバー */}
      <div className="mountain-log-filters">
        <input
          type="text"
          className="mountain-log-search"
          placeholder="山名・読みで検索..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="mountain-log-pref-filter"
          value={prefFilter}
          onChange={(e) => setPrefFilter(e.target.value)}
        >
          <option value="">都道府県（全て）</option>
          {prefectures.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <div className="mountain-log-filter-btns">
          {[
            { value: 'all', label: 'すべて' },
            { value: 'climbed', label: '登頂済み' },
            { value: 'unclimbed', label: '未登頂' },
          ].map(({ value, label }) => (
            <button
              key={value}
              className={`mountain-log-filter-btn ${climbedFilter === value ? 'active' : ''}`}
              onClick={() => setClimbedFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* 件数表示 */}
      <div className="mountain-log-result-count">
        {filteredMountains.length} 件表示
      </div>

      {/* 山リスト */}
      {loadingMountains ? (
        <LoadingSpinner />
      ) : (
        <div className="mountain-log-list">
          {filteredMountains.length === 0 ? (
            <div className="mountain-log-empty">
              該当する山が見つかりません
            </div>
          ) : (
            filteredMountains.map((mountain) => {
              const log = userLogs[mountain.no] || {};
              const isClimbed = !!log.climbed;
              const isExpanded = expandedNo === mountain.no;

              return (
                <div
                  key={`${activeTab}_${mountain.no}`}
                  className={`mountain-log-item ${isClimbed ? 'climbed' : ''} ${isExpanded ? 'expanded' : ''}`}
                >
                  <div
                    className="mountain-log-item-main"
                    onClick={() => setExpandedNo(isExpanded ? null : mountain.no)}
                  >
                    <div className="mountain-log-item-left">
                      <span className="mountain-log-item-no">{mountain.no}</span>
                      <div className="mountain-log-item-info">
                        <span className="mountain-log-item-name">{mountain.name}</span>
                        <span className="mountain-log-item-yomi">{mountain.yomi}</span>
                      </div>
                    </div>
                    <div className="mountain-log-item-right">
                      <div className="mountain-log-item-meta">
                        <span className="mountain-log-item-elevation">
                          {mountain.elevation.toLocaleString()} m
                        </span>
                        <span className="mountain-log-item-pref">{mountain.prefecture}</span>
                      </div>
                      <div className="mountain-log-item-actions" onClick={(e) => e.stopPropagation()}>
                        <label className="mountain-log-checkbox-label">
                          <input
                            type="checkbox"
                            className="mountain-log-checkbox"
                            checked={isClimbed}
                            onChange={() => handleClimbedToggle(mountain.no, mountain.name)}
                            disabled={!currentUser}
                          />
                          <span className={`mountain-log-checkbox-custom ${isClimbed ? 'checked' : ''}`}>
                            {isClimbed ? '✓' : ''}
                          </span>
                          <span className="mountain-log-checkbox-text">
                            {isClimbed ? '登頂済み' : '未登頂'}
                          </span>
                        </label>
                        {log.date && (
                          <span className="mountain-log-item-date">{log.date}</span>
                        )}
                      </div>
                    </div>
                    <span className="mountain-log-expand-icon">{isExpanded ? '▲' : '▼'}</span>
                  </div>

                  {/* 展開パネル */}
                  {isExpanded && (
                    <div className="mountain-log-item-detail">
                      {!currentUser ? (
                        <p className="mountain-log-login-msg">
                          記録を保存するにはログインが必要です
                        </p>
                      ) : (
                        <div className="mountain-log-edit-form">
                          <div className="mountain-log-edit-row">
                            <label className="mountain-log-edit-label">
                              <input
                                type="checkbox"
                                checked={isClimbed}
                                onChange={() => handleClimbedToggle(mountain.no, mountain.name)}
                              />
                              <span>登頂済み</span>
                            </label>
                          </div>
                          {isClimbed && (
                            <>
                              <div className="mountain-log-edit-row">
                                <label className="mountain-log-edit-label">登頂日</label>
                                <input
                                  type="date"
                                  className="mountain-log-edit-input"
                                  value={log.date || ''}
                                  onChange={(e) =>
                                    handleFieldChange(mountain.no, 'date', e.target.value)
                                  }
                                />
                              </div>
                              <div className="mountain-log-edit-row">
                                <label className="mountain-log-edit-label">思い出メモ</label>
                                <textarea
                                  className="mountain-log-edit-textarea"
                                  rows={3}
                                  placeholder="山行の思い出を記録..."
                                  value={log.memo || ''}
                                  onChange={(e) =>
                                    handleFieldChange(mountain.no, 'memo', e.target.value)
                                  }
                                />
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
      {loadingLogs && (
        <div className="mountain-log-logs-loading">記録を読み込み中...</div>
      )}
    </div>
  );
}

export default MountainLog;
